import { AnalyticsEvent } from '@oy/analytics';
import { Colors } from '@oy/uicore';
import { Dropdown, FontStyle, Text, TextField } from '@oy/uidesktop';
import { SystemIcon } from '@oy/uiicon';
import countryCodePackage from 'country-codes-list';
import { useEffect, useRef, useState } from 'react';
import { useTranslate } from 'src/hooks';
import { PicInformationFieldName as FieldName } from '../../constants';
import styles from './CountryCodeDropdown.module.css';

export type ModalCountryCodeProps = {
  setField: (key: string, value: any) => void;
  trackEvent?: (event: AnalyticsEvent) => void;
  value: string;
};

export type CountryCode = {
  code: string;
  country: string;
};

const CountryCodeDropdown = ({
  setField,
  trackEvent,
  value,
}: ModalCountryCodeProps) => {
  const [show, setShow] = useState(false);
  const { getTranslation } = useTranslate();
  const [selectedCountryCode, setSelectedCountryCode] = useState(value);
  const [searchedCountry, setSearchedCountry] = useState('');
  const [countryCodeList, setCountryCodeList] = useState<CountryCode[]>([
    { code: '+62', country: 'Indonesia' },
  ]);
  const menuRef = useRef<HTMLInputElement>(null);

  const loadCountryCodeList = () => {
    let tempList: CountryCode[] = [];
    const countryList = countryCodePackage.customList(
      // @ts-expect-error We should pass enum here, but the enum isn't exported by the lib
      'countryNameEn',
      '+{countryCallingCode}',
    );
    Object.keys(countryList).forEach(key => {
      if (
        !(key in tempList) &&
        countryList[key as keyof typeof countryList] !== '+62'
      ) {
        tempList.push({
          code: countryList[key as keyof typeof countryList],
          country: key,
        });
      }
    });

    setCountryCodeList([...countryCodeList, ...tempList]);
  };

  useEffect(() => {
    loadCountryCodeList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFilterCountryCodeList = (e: React.FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLTextAreaElement;
    setSearchedCountry(target.value);
  };

  const onSelectedCountryCode = (value: string, analytics: AnalyticsEvent) => {
    setSelectedCountryCode(value);
    setField(FieldName.CountryCode, value);
    trackEvent?.(analytics);
    setShow(false);
  };

  const onClickButton = () => {
    setShow(!show);
  };

  useEffect(() => {
    let handler = (e: any) => {
      if (!menuRef.current?.contains(e.target)) {
        setShow(false);
      }
    };
    document.addEventListener('mousedown', handler);

    return () => document.removeEventListener('mousedown', handler);
  });

  return (
    <div ref={menuRef}>
      <Dropdown>
        <div
          id="lstCountryCode"
          className={styles.countryCodeButton}
          onClick={onClickButton}
        >
          <Text
            textStyle={Text.Style.BodyMdRegular}
            style={{ margin: 'auto 2px' }}
          >
            {selectedCountryCode}
          </Text>
          <div style={{ margin: 'auto 0' }}>
            <SystemIcon name="arrow-down" color={Colors.BLACK80} size={14} />
          </div>
        </div>
        <Dropdown.Menu
          style={{
            width: '280px',
            boxShadow: '0px 2px 6px rgba(30, 43, 55, 0.2)',
            maxHeight: '560px',
            visibility: `${show ? 'visible' : 'hidden'}`,
          }}
          isAbsolute
        >
          <div className={styles.countryCodeSearchContainer}>
            <TextField
              id="iptCountryCode"
              placeholder={getTranslation('search_country_code')}
              fontStyle={FontStyle.gray}
              value={searchedCountry}
              onChange={e => onFilterCountryCodeList(e)}
              rightIcon={
                <SystemIcon name="search" color={Colors.BLACK80} size={14} />
              }
            />
          </div>

          <Dropdown.MenuItemDivider />
          <div className={styles.countryCodeItemContainer}>
            {countryCodeList
              .filter(item => {
                return searchedCountry.toLowerCase() === ''
                  ? item
                  : item.country.toLowerCase().includes(searchedCountry);
              })
              .map((item, index) => {
                return (
                  <div
                    className={styles.countryCodeItem}
                    onClick={() =>
                      onSelectedCountryCode(
                        item.code,
                        AnalyticsEvent.input_country_code,
                      )
                    }
                    key={index}
                  >
                    <Text
                      textStyle={Text.Style.BodyMdRegular}
                      color={
                        item.code === selectedCountryCode
                          ? Colors.BLUE100
                          : Colors.BLACK100
                      }
                    >
                      {item.country}
                    </Text>
                    <Text
                      textStyle={Text.Style.BodyMdRegular}
                      color={
                        item.code === selectedCountryCode
                          ? Colors.BLUE100
                          : Colors.BLACK100
                      }
                    >
                      {item.code}
                    </Text>
                  </div>
                );
              })}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default CountryCodeDropdown;
